import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import {
  amendDonation,
  decideDonation,
  marketPromotionQueryKeys,
  recommendDonation,
  verifyMis,
} from '../api/marketPromotion.api';
import { DONATION_DECISION } from '../constants/marketPromotion.constants';
import type { DecideDonationPayload, VerifyMisPayload } from '../types/marketPromotion.types';

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: marketPromotionQueryKeys.all() });
  };
}

export function useDecideDonation(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ detailId, payload }: { detailId: number; payload: DecideDonationPayload }) =>
      decideDonation(detailId, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(
        variables.payload.approveTypeNo === DONATION_DECISION.APPROVED
          ? 'Donation approved'
          : 'Donation rejected',
      );
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to record the decision'),
  });
}

export function useRecommendDonation(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      detailId,
      payload,
    }: {
      detailId: number;
      payload: Omit<DecideDonationPayload, 'approveTypeNo'>;
    }) => recommendDonation(detailId, payload),
    onSuccess: () => {
      invalidate();
      toast.success('Donation recommended');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to recommend the donation'),
  });
}

export function useAmendDonation(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      detailId,
      payload,
    }: {
      detailId: number;
      payload: Omit<DecideDonationPayload, 'approveTypeNo'>;
    }) => amendDonation(detailId, payload),
    onSuccess: () => {
      invalidate();
      toast.success('Donation corrected');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to correct the donation'),
  });
}

export function useVerifyMis(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ detailId, payload }: { detailId: number; payload: VerifyMisPayload }) =>
      verifyMis(detailId, payload),
    onSuccess: () => {
      invalidate();
      toast.success('Call recorded');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to record the call'),
  });
}
